import { computed, ref, watch } from 'vue'

const STORAGE_KEY = 'beiyidata-locale'
const SUPPORTED_LOCALES = ['zh', 'en', 'es', 'fr', 'ja']

const localeNames = {
  zh: '中文',
  en: 'EN',
  es: 'ES',
  fr: 'FR',
  ja: 'JP',
}

const seoContent = {
  title: {
    zh: 'Beiyi Data · AI 工具、移动应用与企业数据产品',
    en: 'Beiyi Data · AI Tools, Mobile Apps & Enterprise Data Products',
    es: 'Beiyi Data · Herramientas IA, Apps móviles y Productos de datos empresariales',
    fr: "Beiyi Data · Outils IA, Apps mobiles et Produits data d'entreprise",
    ja: 'Beiyi Data · AIツール・モバイルアプリ・エンタープライズデータ製品',
  },
  description: {
    zh: '贝易科技官网，涵盖 AI Tools、Consumer Apps 与 Enterprise Data。',
    en: 'Official website for Beiyi Data products across AI tools, consumer apps, and enterprise data.',
    es: 'Sitio oficial de Beiyi Data con herramientas IA, apps de consumo y datos empresariales.',
    fr: "Site officiel de Beiyi Data couvrant outils IA, apps grand public et data d'entreprise.",
    ja: 'AIツール、コンシューマーアプリ、企業データを提供する Beiyi Data の公式サイト。',
  },
}

const detectLocale = () => {
  if (typeof window === 'undefined') {
    return 'zh'
  }

  const saved = window.localStorage.getItem(STORAGE_KEY)
  if (SUPPORTED_LOCALES.includes(saved)) {
    return saved
  }

  const lang = window.navigator.language.toLowerCase()
  if (lang.startsWith('zh')) return 'zh'
  if (lang.startsWith('es')) return 'es'
  if (lang.startsWith('fr')) return 'fr'
  if (lang.startsWith('ja')) return 'ja'
  return 'en'
}

const locale = ref(detectLocale())

const resolveText = (value, currentLocale) => {
  if (typeof value === 'string') return value
  if (!value || typeof value !== 'object') return ''

  return (
    value[currentLocale]
    ?? value.en
    ?? value.zh
    ?? value.es
    ?? value.fr
    ?? value.ja
    ?? ''
  )
}

const resolveList = (value, currentLocale) => {
  if (Array.isArray(value)) return value
  if (!value || typeof value !== 'object') return []

  return (
    value[currentLocale]
    ?? value.en
    ?? value.zh
    ?? value.es
    ?? value.fr
    ?? value.ja
    ?? []
  )
}

watch(
  locale,
  (next) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, next)
    }

    if (typeof document !== 'undefined') {
      const langMap = {
        zh: 'zh-CN',
        en: 'en',
        es: 'es',
        fr: 'fr',
        ja: 'ja',
      }
      document.documentElement.lang = langMap[next] ?? 'en'
      document.title = seoContent.title[next] ?? seoContent.title.en

      const descriptionTag = document.querySelector('meta[name="description"]')
      if (descriptionTag) {
        descriptionTag.setAttribute('content', seoContent.description[next] ?? seoContent.description.en)
      }
    }
  },
  { immediate: true },
)

export const useI18n = () => {
  const t = (value) => resolveText(value, locale.value)
  const ta = (value) => resolveList(value, locale.value)

  const setLocale = (nextLocale) => {
    if (!SUPPORTED_LOCALES.includes(nextLocale)) return
    locale.value = nextLocale
  }

  return {
    locale: computed(() => locale.value),
    locales: SUPPORTED_LOCALES,
    localeNames,
    setLocale,
    t,
    ta,
  }
}
